import { Injectable } from '@angular/core';
import { ChatbotConfig, SearchDoc, Trunk, TrunkItem, Webscrape } from '@shared/index';
import { DocumentChunk } from '@reflact/docsearch';
import { AdminService } from '@shared-angular/services/admin.service';
import { CounterService } from '@shared-angular/services/counter.service';
import { EditorList, EditorListItem } from './editor-items.service';
import { SelectableDocument } from './file-upload.service';
import { FusionService } from '@shared-angular/services/fusion.service';
import { LoginService } from '@shared-angular/services/login.service';
import { McpService } from '@shared-angular/services/mcp.service';
import { ProjectService } from './project.service';
import { SocketAdminService } from '@shared-angular/services/socketadmin.service';
import { StageAdminService } from '@shared-angular/services/stage-admin.service';
import { ToastrService } from '@shared-angular/services/toastr.service';

export const TOOL_TYPE_WENNDANN = "customtoolkit";

@Injectable({
  providedIn: 'root'
})
export class RouteShareService {
  public botConfig!: ChatbotConfig;
  public botImageCacheBuster: number = Date.now();

  // overridden if documentsResolver is called
  public document?: SearchDoc;
  public documentChunks?: DocumentChunk[];

  public documents: SearchDoc[] = [];
  // overridden if datatrunkResolver is called
  public datatrunk?: (Trunk & { items: TrunkItem[] });
  public datatrunks: (Trunk & { items: TrunkItem[] })[] = [];

  // blockly editor
  public editorItem?: EditorListItem;
  public editorItems: EditorList;

  public selectableDocuments: SelectableDocument[] = [];

  public imageTypes = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.tif', '.webp', '.svg'];


  // overridden if webscrapeResolver is called
  public webscrape?: Webscrape;
  public webscrapeMap = new Map<string, {
    _id: string;
    url: string;
    chunk: number;
  }[]>();
  public webscrapes: Webscrape[] = [];

  constructor(
    private toastr: ToastrService,
    private adminService: AdminService,
    private counter: CounterService,
    private socketAdminService: SocketAdminService,
    private loginService: LoginService,
    public projectService: ProjectService,
    private stageAdmin: StageAdminService,
    private fusionService: FusionService,
    private mcpService: McpService
  ) {
    void this.socketAdminService.getIo().then(io => {
      io.on('botConfigChanged', (data) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (this.botConfig && data.config._id === this.botConfig._id) {
          this.botConfig = data.config;
          if (data.by != this.loginService.loggedInUser?.name) {
            this.toastr.info("BOT_UPDATED", `durch ${data.by}`);
          }
        }
        this.projectService.bots = this.projectService.bots.map((bc) => {
          if (bc._id === data.config._id) {
            return data.config;
          }
          return bc;
        });
      });
      io.on('botConfigFieldChanged', (data) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (this.botConfig && data.botId === this.botConfig._id) {
          //@ts-expect-error
          this.botConfig[data.field] = data.value;
          if (data.by != this.loginService.loggedInUser?.name) {
            this.toastr.info("BOT_UPDATED", `durch ${data.by}`);
          }
        }
        this.projectService.bots = this.projectService.bots.map((bc) => {
          if (bc._id === data.botId) {
            //@ts-expect-error
            bc[data.field] = data.value;
          }
          return bc;
        });
      });
    });
    this.editorItems = this.setEmptyEditorList();
  }

  public getLoggedInUser() {
    return this.loginService.loggedInUser;
  }

  public getToolsByType(type: string, onlyEnabledTools: boolean = true) {
    if (onlyEnabledTools) {
      return this.botConfig.toolConfigs.filter((toolConfig) => toolConfig.enabled && toolConfig.tool === type);
    }
    return this.botConfig.toolConfigs.filter((toolConfig) => toolConfig.tool === type);
  }

  /**
    * Saves a single field of the bot configuration by sending a POST request to the server.
    * @returns {Promise<void>} A promise that resolves when the bot configuration is successfully saved.
    */
  public async saveBotConfigField(field: keyof ChatbotConfig) {
    if (typeof this.botConfig[field] === 'string') {
      (this.botConfig as Record<string, unknown>)[field] = (this.botConfig[field]).trim();
    }
    const value: ChatbotConfig[typeof field] = this.botConfig[field];
    if (await this.editBotConfigAllowedOrError()) {
      try {
        const res = await this.adminService.updateBotField(this.botConfig._id, field, value);
        this.botConfig = res;
        this.toastr.success("SAVED");
        await this.loginService.setAdminPosition(this.botConfig._id);
        this.counter.setTools(this.botConfig._id, this.botConfig.toolConfigs.filter((toolConfig) => toolConfig.enabled && (toolConfig.tool === 'schnittstelle' || toolConfig.tool === 'websitesearch')).length);
        this.counter.setWennDann(this.botConfig._id, this.botConfig.toolConfigs.filter((toolConfig) => toolConfig.enabled && toolConfig.tool === 'customtoolkit').length);
        this.counter.setWebhooks(this.botConfig._id, this.botConfig.toolConfigs.filter((toolConfig) => toolConfig.enabled && toolConfig.tool === 'webhook').length);
        this.projectService.bots = this.projectService.bots.map((bc) => {
          if (bc._id === this.botConfig._id) {
            return this.botConfig;
          }
          return bc;
        });
      } catch (error: unknown) {
        const errorType = (error as { error?: { errorType?: string } }).error?.errorType;
        if (errorType === 'SKILL_ID_DUPLICATE') {
          this.toastr.error("SAVED", $localize`:@@error-skill-id-duplicate:Diese Skill-ID wird bereits von einem anderen Skill in diesem Bot verwendet. Wähle eine andere Skill-ID.`);
        } else if (errorType === 'FUNCTION_NAME_DUPLICATE') {
          this.toastr.error("SAVED", $localize`:@@error-function-name-duplicate:Dieser Funktionsname wird bereits von einem anderen Skill in diesem Bot verwendet. Wähle einen anderen Namen.`);
        }
      }
    }
  }

  public async copyIfThen(toolId: string) {
    if (await this.editBotConfigAllowedOrError()) {
      const botConfig = await this.adminService.copyIfThen(this.botConfig._id, toolId);
      this.botConfig = botConfig;
      this.toastr.success("SAVED");
      await this.loginService.setAdminPosition(this.botConfig._id);
      this.counter.setWennDann(this.botConfig._id, this.botConfig.toolConfigs.filter((toolConfig) => toolConfig.enabled && toolConfig.tool === 'customtoolkit').length);
      this.projectService.bots = this.projectService.bots.map((bc) => {
        if (bc._id === this.botConfig._id) {
          return this.botConfig;
        }
        return bc;
      });
    }
  }

  public async editBotConfigAllowedOrError() {
    if (this.userCanEditBotConfig()) return true;
    if (this.projectService.selectedProject?.editMode == "none") {
      this.toastr.error("SAVED", $localize`:@@readOnlyMode:Dieser Bot ist schreibgeschützt`);
    } else {
      this.toastr.error("SAVED", $localize`:@@notCreator:Du bist nicht der Ersteller dieses Bots. Dupliziere ihn, um ihn bearbeiten zu können.`);
    }
    const res = await this.adminService.loadBotConfig(this.botConfig._id);
    if (res) {
      this.botConfig = res;
    }
    return false;
  }

  public userCanEditBotConfig(): boolean {
    if (this.botConfig.isTemplate && this.loginService.hasSUPERAdmin()) { return true; }
    if (!this.projectService.selectedProject) return false;
    if (this.botConfig.thisIsTrash) return false;
    if (this.botConfig.archived) return false;
    return this.projectService.canChange(this.projectService.selectedProject);
  }

  public checkFunctionName(name: string): boolean {
    if (name.trim() == "") return false;
    if (name.length > 30) return false;
    const regex = RegExp(/^[a-zA-Z0-9-_]+$/).test(name);
    if (!regex) this.toastr.error("SAVED", $localize`:@@invalidName:Der Name enthält ungültige Zeichen. Verwende nur Buchstaben, Zahlen und Bindestriche.`);
    return regex;
  }

  // TOLLE GETTER FÜR ALLES ALS STRING

  // ALLES MIT DATEIN
  public _allImageSkills() {
    const foundImages = this.documents.filter((d) => {
      return this.imageTypes.includes(d.filename.substring(d.filename.lastIndexOf('.')));
    }).map((i) => i.skillId);
    return foundImages.filter((s) => s.trim() != "");
  };

  public _allDokuments() {
    const foundImages = this.documents.filter((d) => {
      return !this.imageTypes.includes(d.filename.substring(d.filename.lastIndexOf('.')));
    }).map((i) => i.skillId);
    return foundImages.filter((s) => s.trim() != "");
  };

  public _allWebhooks() {
    return this.getToolsByType("webhook", false).map((wh) => wh.skillId);
  };

  public _allDataBases() {
    return this.datatrunks.map((dt) => dt.skillId);
    //  return this.rss.getToolsByType("customtoolkit", false).map((wh) => wh.skillId);
  };

  public _allFusionViews() {
    return this.fusionService.fusionContent.map((fv) => fv.metadata.name);
  }

  public _allFusionData() {
    return this.fusionService.fusionContent.map((fv) => fv.metadata.name + "|" + fv._id);
  }

  public _allWennDann() {
    return this.getToolsByType("customtoolkit", false).map((wh) => wh.skillId);
  };

  public _allOnlineSources() {
    return this.getToolsByType("websitesearch", false).map((wh) => wh.skillId);
  };

  public _allOfflineWebsites() {
    return this.webscrapes.map((ows) => ows.skillId);
  };

  public _allStages() {
    return this.stageAdmin.stages.map((e) => e.name);
  }

  public _allSkills() {
    const skillsWithSkillId: string[] = [];
    const toolsWithSkillId = this.botConfig.toolConfigs.filter(toolConfig => toolConfig.skillId !== '');
    skillsWithSkillId.push(...toolsWithSkillId.map(toolConfig => toolConfig.skillId));
    const docsWithSkillId = this.documents.filter(doc => doc.skillId !== '');
    skillsWithSkillId.push(...docsWithSkillId.map(doc => doc.skillId));
    const datatrunksWithSkillId = this.datatrunks.filter(datatrunk => datatrunk.skillId !== '');
    skillsWithSkillId.push(...datatrunksWithSkillId.map(datatrunk => datatrunk.skillId));
    const webscrapesWithSkillId = this.webscrapes.filter(webscrape => webscrape.skillId !== '');
    skillsWithSkillId.push(...webscrapesWithSkillId.map(webscrape => webscrape.skillId + ''));
    skillsWithSkillId.push(...this.mcpService.mcps.map(mcp => {
      if (mcp.type === "external") {
        return mcp.tool.allowed_tools.map(t => mcp.tool.server_label + "_" + t);
      } else {
        return mcp.allowed_tools.map(t => mcp.toolName + "_" + t);
      }
    }).reduce((acc, current) => {
      acc.push(...current);
      return acc;
    }, []));
    return skillsWithSkillId;
  }

  public setEmptyEditorList(): EditorList {
    return {
      global: {
        code: '',
        isDirty: false,
        name: 'JS Global',
        uniqueId: '',
        xml: '',
        onFinishMethod: async (_c) => { return new Promise((resolve, _reject) => { resolve(); }); }
      },
      stages: [],
      fusion: [],
      ifthen: []
    };
  }
}
